import { getUserInfo } from '../../auth/keycloak';
import { enqueueFeEvent, updateFePageContext } from './queue';
import { resolvePageContext, resolveStudentRouteAction } from './routeContext';
import { reportStudentAction } from './studentActions';

let behaviorInitialized = false;
let lastUiClick: { type: string; label?: string; selector?: string; path?: string; timestamp: string } | null = null;

export function getLastUiAction() {
  return lastUiClick;
}
let pageEnteredAt = Date.now();
let lastPathname = '';
let lastReferrerPath = '';

function isStudentUser(): boolean {
  const user = getUserInfo();
  return Boolean(user?.roles?.includes('STUDENT') && !user.roles.includes('TEACHER') && !user.roles.includes('ADMIN'));
}

function cssSelectorSummary(element: Element): string {
  const telemetryAction = element.getAttribute('data-telemetry-action');
  if (telemetryAction) return `[data-telemetry-action=${telemetryAction}]`;
  const id = element.id ? `#${element.id}` : '';
  const classNames = (element.className || '').toString().trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const cls = classNames.length > 0 ? `.${classNames.join('.')}` : '';
  return `${element.tagName.toLowerCase()}${id}${cls}`;
}

function clickLabel(element: Element): string | undefined {
  const explicit =
    element.getAttribute('data-telemetry-action') ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title');
  if (explicit?.trim()) return explicit.trim().slice(0, 120);
  const text = element.textContent?.trim();
  return text ? text.slice(0, 120) : undefined;
}

function emitPageLeave(pathname: string) {
  const dwell = Math.max(0, Date.now() - pageEnteredAt);
  if (dwell < 300) return;
  const ctx = resolvePageContext(pathname);
  enqueueFeEvent({
    k: 'pl',
    t: new Date().toISOString(),
    path: ctx.path,
    route: ctx.route,
    dwell,
  });
}

function emitPageView(pathname: string) {
  pageEnteredAt = Date.now();
  const ctx = updateFePageContext(pathname);
  enqueueFeEvent({
    k: 'pv',
    t: new Date().toISOString(),
    path: ctx.path,
    route: ctx.route,
    ref: lastReferrerPath || document.referrer || undefined,
  });

  if (!isStudentUser()) return;

  const routeAction = resolveStudentRouteAction(pathname);
  if (routeAction) {
    reportStudentAction(routeAction.action, {
      assignmentCode: routeAction.asn,
      classroomCode: routeAction.cls,
      pathname,
    });
  }
}

export function trackRouteChange(pathname: string) {
  if (pathname === lastPathname) return;
  if (lastPathname) {
    emitPageLeave(lastPathname);
    lastReferrerPath = lastPathname;
  }
  lastPathname = pathname;
  emitPageView(pathname);
}

export function initFeBehaviorTracking() {
  if (behaviorInitialized || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  behaviorInitialized = true;

  trackRouteChange(window.location.pathname);

  document.addEventListener(
    'click',
    (evt) => {
      const target = evt.target instanceof Element ? evt.target : null;
      const actionable = target?.closest(
        'button, [role="button"], a, input[type="submit"], [data-telemetry-action], [data-testid]',
      );
      const element = actionable || target;
      if (!element) return;

      enqueueFeEvent({
        k: 'clk',
        t: new Date().toISOString(),
        el: cssSelectorSummary(element),
        lb: clickLabel(element),
        act: element.getAttribute('data-telemetry-action') || undefined,
        tag: element.tagName.toLowerCase(),
      });

      lastUiClick = {
        type: 'click',
        label: clickLabel(element),
        selector: cssSelectorSummary(element),
        path: window.location.pathname,
        timestamp: new Date().toISOString(),
      };
    },
    { capture: true, passive: true },
  );

  document.addEventListener('visibilitychange', () => {
    enqueueFeEvent({
      k: 'tab',
      t: new Date().toISOString(),
      vis: document.visibilityState === 'visible' ? 'visible' : 'hidden',
      hid: document.hidden,
    });
  });
}

export function reportFeInfo(message: string, ctx?: Record<string, unknown>) {
  enqueueFeEvent({
    k: 'log',
    t: new Date().toISOString(),
    lvl: 'info',
    msg: message.slice(0, 500),
    ctx,
  });
}
